import includes from 'lodash/includes'
import { computed, makeObservable } from 'mobx'

import { Model } from 'modules/core/lib/mobx-rest'

import type Employee from 'modules/employees/store/models/Employee'
import { employeeAllowances } from 'modules/timeoff/lib/employeeAllowances'
import { getTimeoffDefaultsLabel } from 'modules/timeoff/lib/leaveTypeLabels'

export const COLOR_CODES = [
  'FF9153',
  'FF869E',
  'FBE3BC',
  '6AC7CE',
  '68CC45',
  '07A2AD',
  '47A7FF',
  '877FED',
  '515164',
  'AEAEB8',
]

export const FIXED_LEAVE_IDENTIFIERS = {
  holiday: 'holiday',
  medical_leave: 'medical_leave',
  sick: 'sick',
  parental: 'parental',
}

export const ALL_SET_LEAVE_NAMES = {
  ...FIXED_LEAVE_IDENTIFIERS,
  compassionate: 'compassionate',
  other: 'other',
  overtime_compensation: 'overtime_compensation',
}

interface ILeaveType {
  visibility?: boolean
  [key: string]: any
}

class LeaveType extends Model<ILeaveType> {
  static urlRoot() {
    return '/leave_types'
  }

  constructor(attr: ILeaveType = {}) {
    super(attr)
    makeObservable(this, {
      canEditDetails: computed,
      isNonPayrollMedical: computed,
      canEditName: computed,
      isMedicalLeave: computed,
      isHoliday: computed,
      isParental: computed,
      isSick: computed,
      isCustom: computed,
      isExcludedInPayroll: computed,
      isWorkable: computed,
      isPayable: computed,
      isRestricted: computed,
      name: computed,
      halfDaysUnitsEnabled: computed,
      maxDaysUnits: computed,
      minDaysUnits: computed,
    })
  }

  get isActive(): boolean {
    return this.attributes.active
  }

  get canEditDetails(): boolean {
    if (this.attributes.editable) return true

    return !includes(Object.values(FIXED_LEAVE_IDENTIFIERS), this.attributes.identifier)
  }

  get isNonPayrollMedical(): boolean {
    return this.isSick || this.isParental
  }

  get canEditName(): boolean {
    return this.attributes.editable
  }

  get isAttachmentOptional(): boolean {
    return this.attributes.attachment ? !this.attributes.is_attachment_mandatory : true
  }

  get isMedicalLeave(): boolean {
    return (
      this.attributes.identifier === FIXED_LEAVE_IDENTIFIERS.medical_leave &&
      !this.attributes.editable
    )
  }

  get isHoliday(): boolean {
    return (
      this.attributes.identifier === FIXED_LEAVE_IDENTIFIERS.holiday && !this.attributes.editable
    )
  }

  get isParental(): boolean {
    return (
      this.attributes.identifier === FIXED_LEAVE_IDENTIFIERS.parental && !this.attributes.editable
    )
  }

  get isSick(): boolean {
    return this.attributes.identifier === FIXED_LEAVE_IDENTIFIERS.sick && !this.attributes.editable
  }

  get isCustom(): boolean {
    return this.attributes.identifier === 'custom'
  }

  get isExcludedInPayroll(): boolean {
    return this.isParental || this.isSick
  }

  get isWorkable(): boolean {
    return this.attributes.workable
  }

  get isPayable(): boolean | null {
    return this.attributes.payable
  }

  get isRestricted(): boolean {
    return this.attributes.restricted
  }

  get name(): string {
    const key = this.attributes.identifier

    if (this.attributes.editable) return this.attributes.name

    return getTimeoffDefaultsLabel(key ?? '')
  }

  getAllowanceForEmployee(employee: Employee | undefined) {
    const allowances = employee ? employeeAllowances(employee) : []

    if (allowances.length === 0) return null

    const leaveTypeAllowanceIds = this.attributes.allowance_ids

    if (!leaveTypeAllowanceIds || leaveTypeAllowanceIds.length === 0) {
      return null
    }

    const allowance = allowances.find((a) => leaveTypeAllowanceIds.includes(a.id))

    if (!allowance) return null

    return allowance
  }

  isHourlyForEmployee(employee: Employee | undefined): boolean {
    const allowance = this.getAllowanceForEmployee(employee)

    return !!(allowance && allowance.isHourly)
  }

  get halfDaysUnitsEnabled(): boolean {
    return this.attributes.half_days_units_enabled
  }

  get maxDaysUnits(): number {
    return Number(this.attributes.max_days_in_cents / 100)
  }

  get minDaysUnits(): number {
    return Number(this.attributes.min_days_in_cents / 100)
  }
}

export default LeaveType
